import React, { useState, useEffect } from 'react';
import {
    Building2,
    Bell,
    Mail,
    Bot,
    RefreshCw
} from 'lucide-react';
import { apiClient, AuthResponse } from '../services/api';
import { Routes, Route, useNavigate, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import Drivers from './Drivers';
import EmailAnalytics from './EmailAnalytics';
import NotFoundPage from './ErrorPages/NotFoundPage';
import UserManagement from './UserManagement';
import NotificationSettings from './NotificationSettings';
import NotificationLogs from './NotificationLogs';
import Reports from './Reports';
import AutomationSettings from './AutomationSettings';
import BulkOperationModal from './BulkOperationModal';
import ConsentSendResultsModal from './Modals/ConsentSendResultsModal';
import OrganisationBranding from './OrganisationBranding';
import SiteRiskPage from './SitesPage';
import Dashboard from './Dashboard';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';

type User = AuthResponse['user'];

interface DashboardProps {
    user: User | null;
    onLogout: () => void;
}

const AppShell: React.FC<DashboardProps> = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const isDashboardPage = location.pathname === '/dashboard';

    const [bulkOperationId, setBulkOperationId] = useState<string | null>(null);
    const [isBulkOperationModalOpen, setIsBulkOperationModalOpen] = useState(false);

    const [consentResultsData, setConsentResultsData] = useState<any>(null);
    const [showConsentResultsModal, setShowConsentResultsModal] = useState(false);

    useEffect(() => {
        const bulkOpId = searchParams.get('bulkOperationId');
        if (bulkOpId && isDashboardPage) {
            setBulkOperationId(bulkOpId);
            setIsBulkOperationModalOpen(true);
            searchParams.delete('bulkOperationId');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, isDashboardPage, setSearchParams]);

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
            </div>
        );
    }

    const handleLogout = () => {
        apiClient.logout();
        onLogout();
    };

    return (
        <div className="h-screen flex p-2 gap-2 print:p-0 print:gap-0" style={{ backgroundColor: '#18181b' }}>

            {/* Sidebar */}
            <Sidebar
                collapsed={sidebarCollapsed}
                onCollapse={() => setSidebarCollapsed(v => !v)}
                currentPath={location.pathname}
                onNavigate={navigate}
            />

            {/* Main panel */}
            <div className="flex-1 flex flex-col bg-white rounded-2xl overflow-hidden min-h-0 print:rounded-none">

                {/* Page header */}
                <AppHeader
                    currentPath={location.pathname}
                    user={user}
                    onLogout={handleLogout}
                    onConsentResultsClick={(data) => {
                        setConsentResultsData(data);
                        setShowConsentResultsModal(true);
                    }}
                />

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto">
                    <Routes>
                        <Route path="/dashboard" element={<Dashboard user={user} />} />

                        <Route path="/sites" element={<SiteRiskPage />} />

                        <Route path="/drivers" element={<Drivers user={user} />} />

                        <Route path="/reports" element={<Reports user={user} />} />

                        <Route path="/bulk-operations" element={<Navigate to="/drivers" replace />} />

                        <Route path="/user-management" element={<UserManagement user={user} />} />

                        <Route path="/settings" element={
                            <SettingsTabs userRole={user?.role || 'user'} user={user} />
                        } />

                        <Route path="/" element={<Navigate to="/dashboard" replace />} />

                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </main>
            </div>

            {/* Bulk Operation Modal */}
            {isBulkOperationModalOpen && bulkOperationId && (
                <BulkOperationModal
                    bulkOperationId={bulkOperationId}
                    onClose={() => {
                        setIsBulkOperationModalOpen(false);
                        setBulkOperationId(null);
                    }}
                />
            )}

            {/* Consent Send Results Modal */}
            <ConsentSendResultsModal
                isOpen={showConsentResultsModal}
                onClose={() => {
                    setShowConsentResultsModal(false);
                    setConsentResultsData(null);
                }}
                emailsSent={consentResultsData?.emailsSent || 0}
                totalImported={consentResultsData?.totalDrivers || 0}
                driversWithoutEmail={consentResultsData?.driversWithoutEmail || []}
            />
        </div>
    );
};

// Settings Tabs Component
interface SettingsTabsProps {
    userRole: string;
    user: User | null;
    onAutomationSettingsChange?: (settings: any) => void;
}

const SettingsTabs: React.FC<SettingsTabsProps> = ({ userRole, user, onAutomationSettingsChange }) => {
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(tabFromUrl || 'organisation');

    useEffect(() => {
        if (tabFromUrl) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl]);

    const tabs = [
        { id: 'organisation', label: 'Organisation', icon: Building2 },
        ...(user?.organisation?.automatedChecks ? [{ id: 'automation', label: 'Automated Checks', icon: Bot }] : []),
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'email', label: 'Email Analytics', icon: Mail },
    ];

    return (
        <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
            <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                            isActive
                                                ? 'border-green-500 text-gray-900'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                        style={isActive ? { borderColor: '#69BE28', color: '#231f20' } : {}}
                                    >
                                        <Icon className="w-5 h-5 mr-2" />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                    <div className="p-6">
                        {activeTab === 'email' && <EmailAnalytics userRole={userRole} />}
                        {activeTab === 'notifications' && <NotificationsTab />}
                        {activeTab === 'organisation' && <OrganisationBranding user={user} />}
                        {activeTab === 'automation' && user?.organisation?.automatedChecks && (
                            <AutomationSettings user={user} onSettingsChange={onAutomationSettingsChange} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Notifications Tab Component (wrapper for Settings page)
const NotificationsTab: React.FC = () => {
    const [activeSubTab, setActiveSubTab] = useState('settings');
    const [logRefreshKey, setLogRefreshKey] = useState(0);

    const subTabs = [
        { id: 'settings', label: 'Notification Settings' },
        { id: 'logs', label: 'Notification History' },
    ];

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                    {subTabs.map((tab) => {
                        const isActive = activeSubTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    isActive
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
                {activeSubTab === 'logs' && (
                    <button
                        onClick={() => setLogRefreshKey(k => k + 1)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                )}
            </div>
            <div>
                {activeSubTab === 'settings' && <NotificationSettings user={null} />}
                {activeSubTab === 'logs' && <NotificationLogs user={null} refreshKey={logRefreshKey} />}
            </div>
        </div>
    );
};

export default AppShell;
